import React, { useEffect, useState, useMemo, useContext, useCallback } from "react";
import moment from "moment";
import {
  Card,
  Col,
  Row,
  Tabs,
  Drawer,
  DatePicker,
  Modal,
} from "antd";
import ActionBar from "../components/ActionBar";
import "./styles.css";
import CustomList from "../components/List";
import ListItem from "../components/ListItem";
import generalRecommendationsAPI from "../../API/general-recommendations";
import { useReportDateRange } from "../../hooks/useReportDateRange";
import useProcuringEntityPackage from "../../hooks/useProcuringEntityPackage";
import { AppContext } from "../../context/AppContext";

const { TabPane } = Tabs;

const useColumnLayouts = (activeTab) => {
  return useMemo(() => {
    const baseLayout = { xxl: 10, xl: 10, lg: 8, md: 6, sm: 6, xs: 6 };
    
    if (activeTab === "CSC") {
      return {
        recommendationLayout: { 
          xxl: 10, xl: 10, lg: 10, md: 10, sm: 24, xs: 24 
        },
        serialLayout: { 
          xxl: 3, xl: 3, lg: 3, md: 3, sm: 24, xs: 24 
        },
        reportDateLayout: { 
          xxl: 10, xl: 10, lg: 10, md: 8, sm: 24, xs: 24 
        },
      };
    }
  
    if (activeTab === "PIT" || activeTab === "PCT") {
      return {
        recommendationLayout: { 
          xxl: 8, xl: 8, lg: 8, md: 8, sm: 24, xs: 24 
        },
        serialLayout: { 
          xxl: 2, xl: 2, lg: 2, md: 2, sm: 24, xs: 24 
        },
        reportedByLayout: { 
          xxl: 4, xl: 4, lg: 4, md: 4, sm: 24, xs: 24 
        },
        workingSectionLayout: { 
          xxl: 4, xl: 4, lg: 4, md: 4, sm: 24, xs: 24 
        },
        visitDateLayout: { 
          xxl: 4, xl: 4, lg: 4, md: 4, sm: 24, xs: 24 
        },
      };
    }
    
    return {
      recommendationLayout: baseLayout,
      serialLayout: { xxl: 2, xl: 2, lg: 2, md: 2, sm: 24, xs: 24 },
      reportedByLayout: { xxl: 4, xl: 4, lg: 4, md: 4, sm: 24, xs: 24 },
      workingSectionLayout: { xxl: 4, xl: 4, lg: 4, md: 4, sm: 24, xs: 24 },
      reportDateLayout: { xxl: 4, xl: 4, lg: 4, md: 4, sm: 24, xs: 24 },
      visitDateLayout: { xxl: 4, xl: 4, lg: 4, md: 4, sm: 24, xs: 24 }
    };
  }, [activeTab]);
};

const tabStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 500,
};

const iconStyle = {
  width: 24,
  height: 24,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "9px",
  fontWeight: "700",
  color: "white",
};

function GeneralRecommendations({ match }) {
  const {
    params: { packageId },
  } = match;
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [activeTab, setActiveTab] = useState("CSC");
  const [singlePreviewVisible, setSinglePreviewVisible] = useState(false);
  const [previewImage] = useState("");

  const [cscData, setCscData] = useState([]);
  const [pitData, setPitData] = useState([]);
  const [pctData, setPctData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Use report as source of truth for date range with hook fallback
  const { selectedReportDetails } = useContext(AppContext);
  const { startDate, endDate } = useReportDateRange();
  const globalStartDate = selectedReportDetails?.start || startDate;
  const globalEndDate = selectedReportDetails?.end || endDate;

  const { procuringEntityPackage } = useProcuringEntityPackage(packageId);
  const councilId = procuringEntityPackage?.procuring_entity?.council?.id;
  const councilName = procuringEntityPackage?.procuring_entity?.council?.name || "";

  const { recommendationLayout, serialLayout, reportedByLayout, workingSectionLayout, reportDateLayout, visitDateLayout } = useColumnLayouts(activeTab);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchData = useCallback(async () => {
    if (!globalStartDate || !globalEndDate || !councilId) return;
    
    setLoading(true);
    try {
      const cscResponse = await generalRecommendationsAPI.getCSCGeneralRecommendations(
        councilId,
        moment(globalStartDate).format('YYYY-MM-DD'),
        moment(globalEndDate).format('YYYY-MM-DD')
      );
      setCscData(cscResponse.data?.data?.data || []);

      const pitResponse = await generalRecommendationsAPI.getPITGeneralRecommendations(
        councilId,
        moment(globalStartDate).format('YYYY-MM-DD'),
        moment(globalEndDate).format('YYYY-MM-DD')
      );
      setPitData(pitResponse.data?.data?.data || []);

      const pctResponse = await generalRecommendationsAPI.getPCTGeneralRecommendations(
        councilId,
        moment(globalStartDate).format('YYYY-MM-DD'),
        moment(globalEndDate).format('YYYY-MM-DD')
      );
      setPctData(pctResponse.data?.data?.data || []);
    } catch (error) {
      console.error("Error fetching general recommendations:", error);
    } finally {
      setLoading(false);
    }
  }, [councilId, globalStartDate, globalEndDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRowClick = (item) => {
    setActiveItem(item);
    setDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setActiveItem(null);
  };

  // Preview controls exposed via future actions when needed

  const handleSingleCancel = () => {
    setSinglePreviewVisible(false);
  };

  const getColumns = () => {
    if (activeTab === "CSC") {
      return [
        { ...serialLayout, header: "S/N" },
        { ...recommendationLayout, header: "Recommendation" },
        { ...reportDateLayout, header: "Report Date" }
      ];
    }
    if (activeTab === "PIT") {
      return [
        { ...serialLayout, header: "S/N" },
        { ...recommendationLayout, header: "Recommendation" },
        { ...reportedByLayout, header: "Reported By" },
        { ...workingSectionLayout, header: "Working Section" },
        { ...visitDateLayout, header: "Reported Date" }
      ];
    }
    if (activeTab === "PCT") {
      return [
        { ...serialLayout, header: "S/N" },
        { ...recommendationLayout, header: "Recommendation" },
        { ...reportedByLayout, header: "Reported By" },
        { ...workingSectionLayout, header: "Working Section" },
        { ...visitDateLayout, header: "Visit Date" }
      ];
    }
    return [];
  };

  const getTabContent = (key) => {
    const formatDateRange = () => {
      if (globalStartDate && globalEndDate) {
        return `${moment(globalStartDate).format('DD MMM YYYY')} - ${moment(globalEndDate).format('DD MMM YYYY')}`;
      }
      return 'No date range selected';
    };

    switch (key) {
      case "CSC":
        return (
          <div
            style={{
              backgroundColor: "#f8f9fa",
              display: "flex",
              padding: "20px",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h2 className="table-title">
                CSC GENERAL RECOMMENDATIONS
              </h2>
              <h4 className="table-sub-title">
                Construction Supervision Consultant Report
              </h4>
            </div>
            <div style={{ minWidth: "300px", textAlign: "right" }}>
              <div style={{ fontSize: "14px", color: "#666", marginBottom: "4px" }}>
                Reported on
              </div>
              <div style={{ fontSize: "16px", fontWeight: "500", color: "#333" }}>
                {formatDateRange()}
              </div>
            </div>
          </div>
        );
      case "PIT":
        return (
          <div
            style={{
              backgroundColor: "#f8f9fa",
              display: "flex",
              padding: "20px",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h2 className="table-title">
                PIT GENERAL RECOMMENDATIONS
              </h2>
              <h4 className="table-sub-title">
                Project Implementation Team Report
              </h4>
            </div>
            <div style={{ minWidth: "300px" }}>
              <DatePicker.RangePicker
                value={globalStartDate && globalEndDate ? [moment(globalStartDate), moment(globalEndDate)] : null}
                placeholder={["Start Date", "End Date"]}
                size="middle"
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
              />
            </div>
          </div>
        );
      case "PCT":
        return (
          <div
            style={{
              backgroundColor: "#f8f9fa",
              marginInline: "-20px",
              display: "flex",
              padding: "20px",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h2 className="table-title">
                PCT GENERAL RECOMMENDATIONS
              </h2>
              <h4 className="table-sub-title">
                Project Coordination Team Report
              </h4>
            </div>
            <div style={{ minWidth: "300px" }}>
              <DatePicker.RangePicker
                value={globalStartDate && globalEndDate ? [moment(globalStartDate), moment(globalEndDate)] : null}
                placeholder={["Start Date", "End Date"]}
                size="middle"
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case "CSC":
        return cscData;
      case "PIT":
        return pitData;
      case "PCT":
        return pctData;
      default:
        return [];
    }
  };

  const renderCSCListItem = ({ item, index }) => {
    const truncatedRecommendation = item.recommendation 
      ? (item.recommendation.length > 60 
          ? `${item.recommendation.substring(0, 50)}...` 
          : item.recommendation)
      : 'No recommendation available';
    
    return (
      <ListItem
        key={item.id}
        name={`CSC Recommendation #${item.id}`}
        item={item}
        onClick={() => handleRowClick(item)}
        style={{ marginBottom: 10, cursor: "pointer" }}
      >
        <Row gutter={[8, 0]} style={{ width: '100%' }}>
          <Col {...serialLayout} className="contentEllipse">
            <div className="mobile-label">S/N</div>
            {index + 1}
          </Col>
          <Col {...recommendationLayout} className="contentEllipse">
            <div className="mobile-label">Recommendation</div>
            {truncatedRecommendation}
          </Col>
          <Col {...reportDateLayout} className="contentEllipse">
            <div className="mobile-label">Report Date</div>
            {item.report_start_date && item.report_end_date 
              ? `${moment(item.report_start_date).format('DD MMM YYYY')} - ${moment(item.report_end_date).format('DD MMM YYYY')}`
              : 'No date available'
            }
          </Col>
        </Row>
      </ListItem>
    );
  };

  const renderPITListItem = ({ item, index }) => {
    const truncatedRecommendation = item.general_observations 
      ? (item.general_observations.length > 40 
          ? `${item.general_observations.substring(0, 40)}...` 
          : item.general_observations)
      : 'No recommendation available';
    
    return (
      <ListItem
        key={item.id}
        name={`PIT Recommendation #${item.id}`}
        item={item}
        onClick={() => handleRowClick(item)}
        style={{ marginBottom: 10, cursor: "pointer" }}
      >
        <Row gutter={[8, 0]} style={{ width: '100%' }}>
          <Col {...serialLayout} className="contentEllipse">
            <div className="mobile-label">S/N</div>
            {index + 1}
          </Col>
          <Col {...recommendationLayout} className="contentEllipse">
            <div className="mobile-label">Recommendation</div>
            {truncatedRecommendation}
          </Col>
          <Col {...reportedByLayout} className="contentEllipse">
            <div className="mobile-label">Reported By</div>
            {item.staff_name || item.user?.name || 'Unknown Reporter'}
          </Col>
          <Col {...workingSectionLayout} className="contentEllipse">
            <div className="mobile-label">Working Section</div>
            {item.section || 'No section specified'}
          </Col>
          <Col {...visitDateLayout} className="contentEllipse">
            <div className="mobile-label">Reported Date</div>
            {item.visit_date && item.exit_date 
              ? `${moment(item.visit_date).format('DD MMM YYYY')} - ${moment(item.exit_date).format('DD MMM YYYY')}`
              : item.visit_date 
                ? moment(item.visit_date).format('DD MMM YYYY')
                : 'No date available'
            }
          </Col>
        </Row>
      </ListItem>
    );
  };

  const renderPCTListItem = ({ item, index }) => {
    const truncatedRecommendation = item.observations 
      ? (item.observations.length > 40 
          ? `${item.observations.substring(0, 40)}...` 
          : item.observations)
      : 'No recommendation available';
    
    return (
      <ListItem
        key={item.id}
        name={`PCT Recommendation #${item.id}`}
        item={item}
        onClick={() => handleRowClick(item)}
        style={{ marginBottom: 10, cursor: "pointer" }}
      >
        <Row gutter={[8, 0]} style={{ width: '100%' }}>
          <Col {...serialLayout} className="contentEllipse">
            <div className="mobile-label">S/N</div>
            {index + 1}
          </Col>
          <Col {...recommendationLayout} className="contentEllipse">
            <div className="mobile-label">Recommendation</div>
            {truncatedRecommendation}
          </Col>
          <Col {...reportedByLayout} className="contentEllipse">
            <div className="mobile-label">Reported By</div>
            {item.staff_name || item.user?.name || 'Unknown Reporter'}
          </Col>
          <Col {...workingSectionLayout} className="contentEllipse">
            <div className="mobile-label">Working Section</div>
            {item.section || 'No section specified'}
          </Col>
          <Col {...visitDateLayout} className="contentEllipse">
            <div className="mobile-label">Visit Date</div>
            {item.visit_date 
              ? moment(item.visit_date).format('DD MMM YYYY')
              : 'No date available'
            }
          </Col>
        </Row>
      </ListItem>
    );
  };

  const renderListItem = ({ item, index }) => {
    const currentData = getCurrentData();
    const itemIndex = currentData.findIndex(dataItem => dataItem.id === item.id);
    const safeIndex = itemIndex >= 0 ? itemIndex : 0;
    
    switch (activeTab) {
      case "CSC":
        return renderCSCListItem({ item, index: safeIndex });
      case "PIT":
        return renderPITListItem({ item, index: safeIndex });
      case "PCT":
        return renderPCTListItem({ item, index: safeIndex });
      default:
        return null;
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <ActionBar
        actionButtonProp={{
          title: `${councilName} > General Recommendations`,
        }}
      />
      <Card bordered={false} className="custom-container">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane
            tab={
              <span
                style={{
                  ...tabStyle,
                  color: activeTab === "CSC" ? "#6B46C1" : undefined,
                }}
              >
                <span style={{ ...iconStyle, backgroundColor: "#6B46C1" }}>
                  CSC
                </span>
                CSC
              </span>
            }
            key="CSC"
          >
            {getTabContent("CSC")}
            <div className="csc-table-container">
              <div className="mobile-table-header" style={{ display: 'none' }}>
                <div style={{ fontWeight: 600, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>S/N</div>
                <div style={{ fontWeight: 600, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>Recommendation</div>
                <div style={{ fontWeight: 600, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>Report Date</div>
              </div>
              <CustomList
                itemName="Recommendations"
                items={getCurrentData()}
                page={1}
                itemCount={getCurrentData().length}
                loading={loading}
                headerLayout={getColumns()}
                renderListItem={renderListItem}
                showToolbar={false}
              />
            </div>
          </TabPane>
          <TabPane
            tab={
              <span
                style={{
                  ...tabStyle,
                  color: activeTab === "PIT" ? "#38A169" : undefined,
                }}
              >
                <span style={{ ...iconStyle, backgroundColor: "#38A169" }}>
                  PIT
                </span>
                PIT
              </span>
            }
            key="PIT"
          >
            {getTabContent("PIT")}
            <div className="pit-table-container">
              <div className="mobile-table-header" style={{ display: 'none' }}>
                <div style={{ fontWeight: 600, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>S/N</div>
                <div style={{ fontWeight: 600, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>Recommendation</div>
                <div style={{ fontWeight: 600, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>Reported By</div>
                <div style={{ fontWeight: 600, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>Working Section</div>
                <div style={{ fontWeight: 600, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>Reported Date</div>
              </div>
              <CustomList
                itemName="Recommendations"
                items={getCurrentData()}
                page={1}
                itemCount={getCurrentData().length}
                loading={loading}
                headerLayout={getColumns()}
                renderListItem={renderListItem}
                showToolbar={false}
              />
            </div>
          </TabPane>
          <TabPane
            tab={
              <span
                style={{
                  ...tabStyle,
                  color: activeTab === "PCT" ? "#ED8936" : undefined,
                }}
              >
                <span style={{ ...iconStyle, backgroundColor: "#ED8936" }}>
                  PCT
                </span>
                PCT
              </span>
            }
            key="PCT"
          >
            {getTabContent("PCT")}
            <div className="pct-table-container">
              <div className="mobile-table-header" style={{ display: 'none' }}>
                <div style={{ fontWeight: 600, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>S/N</div>
                <div style={{ fontWeight: 600, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>Recommendation</div>
                <div style={{ fontWeight: 600, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>Reported By</div>
                <div style={{ fontWeight: 600, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>Working Section</div>
                <div style={{ fontWeight: 600, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>Visit Date</div>
              </div>
              <CustomList
                itemName="Recommendations"
                items={getCurrentData()}
                page={1}
                itemCount={getCurrentData().length}
                loading={loading}
                headerLayout={getColumns()}
                renderListItem={renderListItem}
                showToolbar={false}
              />
            </div>
          </TabPane>
        </Tabs>

        <Drawer
          title="Recommendation Details"
          placement="right"
          visible={drawerVisible}
          onClose={handleCloseDrawer}
          width={600}
        >
          {activeItem && (
            <>
              <p>
                <strong>Recommendation:</strong> 
                {activeTab === "CSC" && activeItem.recommendation}
                {activeTab === "PIT" && activeItem.general_observations}
                {activeTab === "PCT" && activeItem.observations}
              </p>
              
              {(activeTab === "PIT" || activeTab === "PCT") && (
                <>
                  <p>
                    <strong>Reported by:</strong> {activeItem.staff_name || activeItem.user?.name || 'Unknown'}
                  </p>
                  <p>
                    <strong>Working Section:</strong> {activeItem.section || 'Not specified'}
                  </p>
                  {activeItem.user?.email && (
                    <p>
                      <strong>Email:</strong> {activeItem.user.email}
                    </p>
                  )}
                                     {activeItem.visit_date && (
                     <p>
                       <strong>Visit Date:</strong> {moment(activeItem.visit_date).format('DD MMM YYYY')}
                     </p>
                   )}
                   {activeItem.exit_date && (
                     <p>
                       <strong>Exit Date:</strong> {moment(activeItem.exit_date).format('DD MMM YYYY')}
                     </p>
                   )}
                </>
              )}
              
              {activeTab === "CSC" && (
                <>
                  {activeItem.council?.name && (
                    <p>
                      <strong>Council:</strong> {activeItem.council.name}
                    </p>
                  )}
                  {activeItem.procuring_entity?.project?.name && (
                    <p>
                      <strong>Project:</strong> {activeItem.procuring_entity.project.name}
                    </p>
                  )}
                  {activeItem.report?.report_title && (
                    <p>
                      <strong>Report:</strong> {activeItem.report.report_title} #{activeItem.report.report_number}
                    </p>
                  )}
                                     {activeItem.report_start_date && (
                     <p>
                       <strong>Report Period:</strong> {moment(activeItem.report_start_date).format('DD MMM YYYY')} - {moment(activeItem.report_end_date).format('DD MMM YYYY')}
                     </p>
                   )}
                </>
              )}
            </>
          )}
        </Drawer>

        <Modal
          visible={singlePreviewVisible}
          footer={null}
          onCancel={handleSingleCancel}
          width="50%"
        >
          <img
            alt="Preview"
            style={{ width: "100%" }}
            src={previewImage}
          />
        </Modal>
      </Card>
    </div>
  );
}

export default GeneralRecommendations; 